import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { breakpoints } from 'styles/theme/breakpoints';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { IExternalPicture } from 'shared/types/ExternalPicture';
import { Image } from 'shared/components/Image';
import { Loader } from 'shared/components/Loader';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { t } from 'localization/utils/t';
import { useKeyDown } from 'shared/hooks/useKeyDown';
import { useStoreSelector } from 'store/hooks/storeHooks';

const TP = 'productTemplate.components.imageGallery.GalleryItemDesktop';

interface IGalleryItemDesktopProps {
  centeredViewport?: boolean;
  handleImageLoad?: () => void;
  idx?: number;
  item?: IExternalPicture | { mainPictureUrl: string; altText?: string };
  itemList?: IExternalPicture[] | { mainPictureUrl: string }[] | IBuyBarProductVariant[];
  listLength?: number;
}

const GalleryItemExpandedView = dynamic(
  () => import('productTemplate/components/imageGallery/GalleryItemExpandedView'),
  { ssr: false },
);

export const GalleryItemDesktop = ({
  centeredViewport,
  handleImageLoad,
  idx,
  item,
  itemList,
  listLength,
}: IGalleryItemDesktopProps) => {
  const {
    name,
    color: productColor,
    productType,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const [isExpanded, setIsExpanded] = useState(false);
  const [clickTargetIdx, setClickTargetIdx] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const isFirstChild = idx === 0;
  const isLastChild = idx === listLength - 1;
  const shouldCropImage = !(productType === 'tops' && productColor?.toLowerCase() === 'white');

  const handleClick = (shouldExpand) => {
    setClickTargetIdx(idx);
    setIsExpanded(shouldExpand);
  };

  const close = () => setIsExpanded(false);

  useKeyDown('Escape', close);

  useEffect(() => {
    setIsLoading(true);
  }, [name]);

  const handleOnLoad = () => {
    setIsLoading(false);

    if (typeof handleImageLoad === 'function') {
      handleImageLoad();
    }
  };

  return (
    <GalleryItemDesktop.Wrapper
      isFirstChild={isFirstChild}
      isLastChild={isLastChild}
      centeredViewport={centeredViewport}
    >
      {isLoading && <Loader style={{ width: '50%', position: 'absolute' }} />}
      <GalleryItemDesktop.Image
        alt={'altText' in item ? item.altText : normalizeValue(name)}
        crop={shouldCropImage}
        disableLazyLoad
        fetchpriority={isFirstChild ? 'high' : 'low'}
        key={name}
        loading={isFirstChild ? 'eager' : 'lazy'}
        onClick={() => handleClick(true)}
        onLoad={handleOnLoad}
        placeholderStyles={GalleryItemDesktop.placeholderStyles}
        sizes={`(max-width: ${breakpoints.mediumLarge}px) 350px, 30vw`}
        src={item.mainPictureUrl || ''}
      />
      {isExpanded && (
        <GalleryItemExpandedView
          clickTargetIdx={clickTargetIdx}
          itemList={itemList}
          name={name}
          shouldCropImage={shouldCropImage}
          title={t(`${TP}.fromGoat`, 'From GOAT')}
          onClose={close}
        />
      )}
    </GalleryItemDesktop.Wrapper>
  );
};

const addMarginLeft = css`
  margin-left: 32px;
`;

const addMarginRight = css`
  margin-right: 120px;
`;

const leftAlignedView = css`
  margin-right: 32px;
`;

const centeredView = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
`;

GalleryItemDesktop.Wrapper = styled.div<{
  isFirstChild: boolean;
  isLastChild: boolean;
  centeredViewport: boolean;
}>`
  @media (max-height: 750px) {
    height: 100%;
    padding: 56px 0 0;
  }
  height: 500px;
  ${({ isFirstChild, centeredViewport }) => isFirstChild && !centeredViewport && addMarginLeft}
  ${({ centeredViewport }) => (centeredViewport ? centeredView : leftAlignedView)}
  ${({ isLastChild }) => isLastChild && addMarginRight}
  > img {
    object-fit: contain;
    height: inherit;
    max-width: ${({ centeredViewport }) => (centeredViewport ? '60vw' : 'unset')};
    width: unset;
    z-index: ${({ theme }) => theme.zIndex.pdpImage};
  }
  &:hover {
    cursor: pointer;
  }
`;

GalleryItemDesktop.placeholderStyles = css`
  background-position: bottom 0px left 50% !important;
  background-size: 64px 64px !important;
`;

GalleryItemDesktop.Image = styled(Image)`
  max-height: 950px;
`;
